import React, { Component, useContext } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Translation, useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import classnames from 'classnames';
import _ from 'lodash';
import { titleize } from 'underscore.string';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { LocaleContext } from 'providers/locale-settings-context';
import Alert from 'components/ui/Alert';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import CustomerSearch from 'components/site/customer-search-alt';
import Badge from 'components/v2/Badge';
import {
  assetTypes,
  equipmentTypes,
  equipmentSizes,
  equipmentTypesLocalizationSlugs,
} from 'constants/Equipment';
import moment from 'moment';
import { getCustomerName } from 'helpers/users';

const EQUIPMENT_ID_LENGTH_RANGE = [10, 11];
const _defaultEquipmentIdPlaceholder = `Must be ${EQUIPMENT_ID_LENGTH_RANGE.join(
  ' or ',
)} characters long`;
const DISABLED_ORDER_STATUSES = ['created', 'en_route'];

const disableStatusTooltip = (
  <Tooltip id="equipment_status">
    <Translation>
      {(t, { _i18n }) => <p>{t('EQUIPMENT_STATUS_CAN_NOT_UPDATE_NOTE')}</p>}
    </Translation>
    ,
  </Tooltip>
);

const emptyTooltip = <Tooltip id="empty" bsClass="invisible" />;

const SectionHeader = ({ title, className, children }) => (
  <div className="flex items-center pb-2 mb-2 border-b border-solid border-grey-lighter">
    <label
      className={classnames({
        'form-label': true,
        'm-0': true,
        ...className,
      })}
    >
      {title}
    </label>
    {children}
  </div>
);

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  className: PropTypes.object,
  children: PropTypes.any,
};

SectionHeader.defaultProps = {
  className: {},
};

class EquipmentForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: {},
    };
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (!_.isEqual(this.props.error, props.error)) {
      this.setState((currentState) => {
        const { error: currentError } = currentState;
        return {
          error: _.merge(currentError, props.error),
        };
      });
    }

    if (this.props.success !== props.success) {
      this.setState({ success: props.success });
    }
  }

  getValidationClassName(field) {
    const { error } = this.state;

    return classnames({
      error: error[field],
    });
  }

  getSaveButtonLabel() {
    const { isNew } = this.props;
    if (isNew) {
      return 'ADD_NEW_EQUIPMENT';
    }
    return 'SAVE_EQUIPMENT';
  }

  hasEquipmentOrder() {
    const {
      equipment: { order },
    } = this.props;

    return !!order;
  }

  importDatesDisabled() {
    if (!this.hasEquipmentOrder()) {
      return true;
    }

    const {
      equipment: { order },
    } = this.props;
    return order.order_type !== 'import';
  }

  exportDatesDisabled() {
    if (!this.hasEquipmentOrder()) {
      return true;
    }

    const {
      equipment: { order },
    } = this.props;
    return order.order_type !== 'export';
  }

  handleChange = (field) => (e) =>
    this.props.onFieldChange(field, e.target.value);

  handleToggleChange = (field) => (value) => {
    this.props.onFieldChange(field, value);
  };

  handleDateFieldChange = (field) => (date) => {
    let mapped = null;
    if (date !== null) {
      mapped = date.toISOString();
    }
    this.props.onFieldChange(field, mapped);
  };

  handleSave = () => {
    const error = this.validate();
    if (Object.keys(error).length === 0) {
      this.props.onSave();
    } else {
      this.setState({
        error,
      });
    }
  };
  handleCancel = () => {
    this.props.onCancel();
  };

  validate = () => {
    const error = {};

    const {
      equipment: {
        equipment_id,
        _equipmentType,
        _equipmentSize,
        _type,
        status,
        chassis_owner_type,
        is_private,
        chassis_owner,
      },
      t,
    } = this.props;

    if (!equipment_id) {
      error.equipment_id = t('ENTER_EQUIPMENT_NUMBER');
    } else if (
      _equipmentType !== 'trailer' &&
      !EQUIPMENT_ID_LENGTH_RANGE.includes(equipment_id.length)
    ) {
      error.equipment_id = t('EQUIPMENT_LENGTH_ERROR', {
        startRange: EQUIPMENT_ID_LENGTH_RANGE[0],
        endRange: EQUIPMENT_ID_LENGTH_RANGE[1],
      });
    }

    if (!_equipmentType) {
      error._equipmentType = t('PLEASE_SELECT_EQUIPMENT_TYPE');
    } else if (_equipmentType === 'container') {
      if (!_type) {
        error._type = t('PLEASE_SELECT_ASSET_TYPE');
      }
      if (!_equipmentSize) {
        error._equipmentSize = t('PLEASE_SELECT_EQUIPMENT_SIZE');
      }
    }

    if (!status) {
      error.status = t('PLEASE_SELECT_EQUIPMENT_STATUS');
    }

    const isChassisOwnerTypeRequired =
      _equipmentType === 'chassis' && is_private && !chassis_owner_type;
    if (isChassisOwnerTypeRequired) {
      error.chassis_owner_type = t('PLEASE_SELECT_CHASSIS_OWNER');
    }

    const isCustomerRequired =
      _equipmentType === 'chassis' &&
      is_private &&
      chassis_owner_type &&
      chassis_owner_type === 'customer' &&
      !chassis_owner;
    if (isCustomerRequired) {
      error.chassis_owner = t('PLEASE_SELECT_CUSTOMER');
    }

    return error;
  };

  disableStatusDropdown = () => {
    const { order, _equipmentType } = this.props.equipment;

    if (_equipmentType && _equipmentType === 'container') {
      if (order) {
        const { order_type, status } = order;

        if (
          order_type === 'import' &&
          DISABLED_ORDER_STATUSES.includes(status)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  disableOwnershipDropdown = () => {
    const { is_private } = this.props.equipment;
    return !!is_private;
  };

  renderSuccess() {
    const { success } = this.state;
    const { t } = this.props;

    if (!success) {
      return null;
    }
    return (
      <div className="mb-4">
        <Alert type="success" title={t(success)} />
      </div>
    );
  }

  renderImportDateFields() {
    const disabled = this.importDatesDisabled();
    const {
      equipment: { last_free_date },
      t,
    } = this.props;

    return (
      <div className="mb-4">
        <SectionHeader title={t('IMPORT_DATES')} className={{ 'mr-2': true }}>
          <OverlayTrigger
            overlay={
              <Tooltip id="import-dates-tooltip">
                {t('ATTACHED_IMPORT_ORDER_TO_SET_DATE_FIELDS')}
              </Tooltip>
            }
          >
            <i className="cursor-pointer fa fa-info-circle" />
          </OverlayTrigger>
        </SectionHeader>
        <DateFormField
          label={t('LAST_FREE_DATE')}
          disabled={disabled}
          key="last_free_date"
          selected={last_free_date}
          onChange={this.handleDateFieldChange('last_free_date')}
        />
      </div>
    );
  }

  renderExportDateFields() {
    const disabled = this.exportDatesDisabled();
    const {
      equipment: { earliest_return_date, last_cut_date, earliest_empty_date },
      t,
    } = this.props;

    return (
      <div className="mb-4">
        <SectionHeader title={t('EXPORT_DATES')} className={{ 'mr-2': true }}>
          <OverlayTrigger
            overlay={
              <Tooltip id="export-dates-tooltip">
                {t('ATTACHED_EXPORT_ORDER_TO_SET_DATE_FIELDS')}
              </Tooltip>
            }
          >
            <i className="cursor-pointer fa fa-info-circle" />
          </OverlayTrigger>
        </SectionHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="">
            <DateFormField
              label={t('LAST_CUT_DATE')}
              disabled={disabled}
              key="last_cut_date"
              selected={last_cut_date}
              onChange={this.handleDateFieldChange('last_cut_date')}
            />
          </div>
          <div className="">
            <DateFormField
              label={t('EARLIEST_DATE_TO_GET_EMPTY')}
              disabled={disabled}
              key="earliest_empty_date"
              selected={earliest_empty_date}
              onChange={this.handleDateFieldChange('earliest_empty_date')}
            />
          </div>
          <div className="">
            <DateFormField
              label={t('EARLIEST_RETURN_DATE')}
              disabled={disabled}
              key="earliest_return_date"
              selected={earliest_return_date}
              onChange={this.handleDateFieldChange('earliest_return_date')}
            />
          </div>
        </div>
      </div>
    );
  }

  renderErrors() {
    const { error } = this.state;
    const errorCount = Object.keys(error).length;
    if (errorCount === 0) {
      return null;
    }
    const { t } = this.props;
    return (
      <Alert title={t('ERRORS')} type="error">
        <ul>
          {Object.keys(error).map((key) => (
            <li key={key}>{error[key]}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  renderCharacterCount() {
    const {
      equipment: { equipment_id },
      t,
    } = this.props;

    const length = (equipment_id || '').length;
    const unit = t('CHARACTER', { count: length });

    return (
      <p className="mb-4 text-sm text-grey-dark">
        (<strong>{length}</strong> {unit})
      </p>
    );
  }

  render() {
    const { t } = this.props;
    if (!this.props.equipment) {
      return null;
    }

    let equipmentIdPlaceholder;

    const { equipment, isAdmin } = this.props;

    if (this.props.equipment._equipmentType !== 'trailer') {
      equipmentIdPlaceholder = t('DEFAULT_EQUIPMENT_PLACEHOLDER', {
        startRange: EQUIPMENT_ID_LENGTH_RANGE[0],
        endRange: EQUIPMENT_ID_LENGTH_RANGE[1],
      });
    } else {
      equipmentIdPlaceholder = t('EQUIPMENT_NUMBER');
    }
    return (
      <div id="add-equipment-form">
        {this.renderErrors()}
        <fieldset>
          <div className={this.getValidationClassName('equipment_id')}>
            <label className="mb-1 form-label">{t('EQUIPMENT_NUMBER')}</label>
            <input
              type="text"
              className="w-full mb-2 form-field"
              placeholder={equipmentIdPlaceholder}
              value={this.props.equipment.equipment_id}
              onChange={this.handleChange('equipment_id')}
            />
            {this.renderCharacterCount()}
          </div>
          {this.props.isAdmin && (
            <div className="grid grid-cols-2 gap-4">
              <div className={this.getValidationClassName('_equipmentType')}>
                <label className="block mb-1 form-label">
                  {t('ASSET_TYPE')}
                </label>
                <select
                  onChange={this.handleChange('_equipmentType')}
                  value={this.props.equipment._equipmentType}
                  className="w-full mb-4 form-select"
                >
                  <option value="">{t('SELECT_ASSET_TYPE')}</option>
                  {assetTypes.map((assetType) => (
                    <option key={assetType} value={assetType}>
                      {titleize(t(assetType.toUpperCase()))}
                    </option>
                  ))}
                </select>
              </div>
              <div className={this.getValidationClassName('status')}>
                <label className="block mb-1 form-label">
                  {t('EQUIPMENT_STATUS')}
                </label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    this.disableStatusDropdown()
                      ? disableStatusTooltip
                      : emptyTooltip
                  }
                >
                  <span>
                    <select
                      onChange={this.handleChange('status')}
                      value={this.props.equipment.status || ''}
                      className="w-full mb-4 form-select"
                      disabled={this.disableStatusDropdown()}
                      style={{
                        pointerEvents: this.disableStatusDropdown()
                          ? 'none'
                          : '',
                      }}
                    >
                      <option value="">{t('SELECT_EQUIPMENT_STATUS')}</option>
                      <option value="unavailable" key={0}>
                        {titleize(t('UNAVAILABLE', { count: 1 }))}
                      </option>
                      <option value="available" key={1}>
                        {titleize(t('AVAILABLE', { count: 1 }))}
                      </option>
                      <option value="closed_area" key={2}>
                        {t('CLOSED_AREA')}
                      </option>
                      <option
                        className="equipment-equipmentForm-toggleOnHold"
                        key={3}
                        value="on_hold"
                      >
                        {t('ON_HOLD')}
                      </option>
                    </select>
                  </span>
                </OverlayTrigger>
              </div>
            </div>
          )}
          {isAdmin && equipment?._equipmentType === 'chassis' && (
            <div className="grid grid-cols-2 gap-4">
              <div className={this.getValidationClassName('is_private')}>
                <label className="block mb-1 form-label">{t('PRIVATE')}</label>
                <ToggleSwitch
                  data-testid="equipment-is-private-switch"
                  enabled={!!equipment?.is_private}
                  onChange={this.handleToggleChange('is_private')}
                />
              </div>
              {this.disableOwnershipDropdown() && (
                <div
                  className={this.getValidationClassName('chassis_owner_type')}
                >
                  <label className="block mb-1 form-label">
                    {t('CHASSIS_OWNERSHIP')}
                  </label>
                  <span>
                    <select
                      onChange={this.handleChange('chassis_owner_type')}
                      value={equipment?.chassis_owner_type || ''}
                      className="w-full mb-4 form-select"
                      data-testid="equipment-owner-type-dropdown"
                    >
                      <option value="">
                        {t('SELECT_EQUIPMENT_CHASSIS_OWNERSHIP')}
                      </option>
                      <option value="cargomatic" key={0}>
                        {titleize(t('CARGOMATIC', { count: 1 }))}
                      </option>
                      <option value="customer" key={1}>
                        {titleize(t('CUSTOMER', { count: 1 }))}
                      </option>
                    </select>
                  </span>
                </div>
              )}
              {equipment?.chassis_owner_type === 'customer' && (
                <div className={this.getValidationClassName('chassis_owner')}>
                  <label className="block mb-1 form-label">
                    {t('CUSTOMER')}
                  </label>
                  <CustomerSearch
                    clearOnSelect
                    assignField
                    onChange={(_item, value) => {
                      this.props.onFieldChange('chassis_owner', value);
                    }}
                    onSelect={(_item, value) => {
                      this.props.onFieldChange('chassis_owner', value);
                    }}
                    placeholder={t('SEARCH_FOR_CUSTOMER')}
                    type="customer"
                    data-testid="equipment-owner-type-dropdown"
                  />
                  {equipment?.chassis_owner?._id && (
                    <div className="flex flex-wrap -mx-2 mt-2 mb-4">
                      <div className="px-2 mb-2" key="chassis_owner_name">
                        <Badge
                          color="blue"
                          withRemove
                          onRemove={() => {
                            this.props.onFieldChange('chassis_owner', null);
                          }}
                        >
                          {getCustomerName(equipment?.chassis_owner)}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {this.props.isAdmin && this.hasEquipmentOrder() && (
            <div className="p-4 bg-grey-lightest">
              {this.renderImportDateFields()}
              {this.renderExportDateFields()}
            </div>
          )}
          {this.props.equipment._equipmentType === 'container' && (
            <section className="grid grid-cols-2 gap-4">
              <div className={this.getValidationClassName('_type')}>
                <label className="block mb-1 form-label">
                  {t('EQUIPMENT_TYPE')}
                </label>
                <select
                  onChange={this.handleChange('_type')}
                  value={this.props.equipment._type}
                  className="w-full mb-4 form-select"
                >
                  <option value="">{t('SELECT_EQUIPMENT_TYPE')}</option>
                  {_.map(equipmentTypes, (label, key) => (
                    <option key={key} value={key}>
                      {t(equipmentTypesLocalizationSlugs[key])}
                    </option>
                  ))}
                </select>
              </div>
              <div className={this.getValidationClassName('_equipmentSize')}>
                <label className="block mb-1 form-label">
                  {t('EQUIPMENT_SIZE')}
                </label>
                <select
                  onChange={this.handleChange('_equipmentSize')}
                  value={this.props.equipment._equipmentSize || ''}
                  className="w-full mb-4 form-select"
                >
                  <option value="">{t('SELECT_EQUIPMENT_SIZE')}</option>
                  {equipmentSizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </section>
          )}
        </fieldset>
        {this.renderSuccess()}
        <div className="flex justify-end form-actions">
          <button className="button button-green" onClick={this.handleSave}>
            {t(this.getSaveButtonLabel())}
          </button>
          <button
            className="button button-secondary"
            onClick={this.handleCancel}
          >
            {t('CANCEL')}
          </button>
        </div>
      </div>
    );
  }
}

EquipmentForm.propTypes = {
  isNew: PropTypes.bool,
  isAdmin: PropTypes.bool,
  equipment: PropTypes.object,
  error: PropTypes.object,
  success: PropTypes.string,
  onFieldChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

EquipmentForm.defaultProps = {
  isNew: false,
  isAdmin: false,
};

function DateFormField({
  disabled,
  label,
  selected,
  onChange,
  className,
  placeholder,
}) {
  const { localeData } = useContext(LocaleContext);
  const finalClassName = {
    'form-field': true,
    'mb-4': true,
    ...(className || {}),
  };
  const { t } = useTranslation();
  return (
    <div>
      {label && <label className="mb-1 form-label block">{label}</label>}
      <DatePicker
        wrapperClassName="w-full"
        className={classnames(finalClassName)}
        selected={getMomentDateObj(selected)}
        onChange={onChange}
        disabled={disabled}
        placeholderText={t(placeholder)}
        locale={localeData.languageCodeId}
      />
    </div>
  );
}

DateFormField.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  selected: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.object,
};

DateFormField.defaultProps = {
  disabled: false,
  label: '',
  className: {},
  placeholder: 'ENTER_A_DATE',
};

function getMomentDateObj(dateStr) {
  if (!dateStr) {
    return null;
  }
  return moment(dateStr);
}

export default withTranslation()(EquipmentForm);
